/**
 * Provider keys grouped by what they do (owner request F-26, 2026-10-08).
 *
 * Words only, plus a provider-id → capability map. Which providers a customer can
 * add is still the server catalog on `GET /v1/provider-keys`: a capability group
 * shows only the providers the server listed, and a server id this file does not
 * know falls into "Other". What a provider powers today is its guide's `powers`
 * line (`providerSetupGuides.ts`); `PLANNED_CAPABILITIES` marks the groups where a
 * provider is listed for something not built in Auteur Cloud yet, so the group
 * can say "planned" instead of implying it works.
 *
 * The status lights (F-27) are computed here too, from the vault record the server
 * returned. Green means only "the provider accepted this key when it was checked",
 * never "generation is guaranteed".
 */

export type CapabilityId = 'writing' | 'images' | 'video' | 'voice' | 'avatars' | 'vision' | 'research' | 'other';

export interface Capability {
  id: CapabilityId;
  title: string;
  /** One plain line: what this kind of key does in Auteur. */
  explanation: string;
}

export const CAPABILITIES: readonly Capability[] = [
  { id: 'writing', title: 'Writing & chat (text AI)', explanation: 'Drafting, rewriting, planning and chat in Tasks and the writing studios.' },
  { id: 'images', title: 'Images', explanation: 'Making still images from a prompt in Visual Canvas.' },
  { id: 'video', title: 'Video', explanation: 'Making video clips from text or images in Visual Canvas.' },
  { id: 'voice', title: 'Voice & audio', explanation: 'Narration, character voices, dialogue, sound effects and music in Audio Canvas.' },
  { id: 'avatars', title: 'Avatars / presenters', explanation: 'A talking presenter on screen who reads your script.' },
  { id: 'vision', title: 'Image analysis (vision)', explanation: 'Reading and describing your images, for example a vision scan in Visual Canvas or Prompt Studio.' },
  { id: 'research', title: 'Research', explanation: 'Searching the web and sources for facts. Planned: nothing runs yet. Web research is meant to use the search built into your OpenAI, Gemini or Claude key, so no extra key will be needed. Tavily or Perplexity are optional extras for deeper research.' },
  { id: 'other', title: 'Other', explanation: 'Advanced: keys for things you run yourself.' },
];

/** Every capability group a provider appears in. A provider can be in several. */
export const PROVIDER_CAPABILITIES: Readonly<Record<string, readonly CapabilityId[]>> = {
  openrouter: ['writing'],
  openai: ['writing', 'images', 'vision'],
  anthropic: ['writing', 'vision'],
  google_gemini: ['vision', 'writing', 'images'],
  elevenlabs: ['voice'],
  fish_audio: ['voice'],
  wavespeed: ['video'],
  fal: ['images', 'video'],
  heygen: ['avatars'],
  perplexity: ['research'],
  tavily: ['research'],
  local: ['other'],
};

/** Groups where a provider is listed for something that is not built in Auteur Cloud yet. */
export const PLANNED_CAPABILITIES: Readonly<Record<string, readonly CapabilityId[]>> = {
  google_gemini: ['writing', 'images'],
  fish_audio: ['voice'],
  fal: ['images', 'video'],
  perplexity: ['research'],
  tavily: ['research'],
};

/** The recommended first key for someone who is not sure (F-21). */
export const RECOMMENDED_PROVIDER = 'openrouter';

export function capabilitiesOf(providerId: string): readonly CapabilityId[] {
  return PROVIDER_CAPABILITIES[providerId] || ['other'];
}

export function isPlannedFor(providerId: string, capability: CapabilityId): boolean {
  return (PLANNED_CAPABILITIES[providerId] || []).includes(capability);
}

/**
 * The server-listed providers in one capability group, in server order. `providers` is the
 * server catalog (`{ id, label, ... }`); an id with no mapping lands in "other".
 */
export function providersIn<T extends { id: string }>(providers: readonly T[], capability: CapabilityId): T[] {
  return providers.filter((item) => capabilitiesOf(item.id).includes(capability));
}

/**
 * The groups in display order. With `focus`, those groups come first (in the order given) and
 * are the only ones marked `focused`; the rest keep their usual order behind "Show all providers".
 */
export function orderedCapabilities(focus?: readonly CapabilityId[] | null): { capability: Capability; focused: boolean }[] {
  const wanted = (focus || []).filter((id) => CAPABILITIES.some((item) => item.id === id));
  if (wanted.length === 0) return CAPABILITIES.map((capability) => ({ capability, focused: true }));
  const first = wanted.map((id) => CAPABILITIES.find((item) => item.id === id) as Capability);
  const rest = CAPABILITIES.filter((item) => !wanted.includes(item.id));
  return [
    ...first.map((capability) => ({ capability, focused: true })),
    ...rest.map((capability) => ({ capability, focused: false })),
  ];
}

// ---------------------------------------------------------------------------
// Traffic lights (F-27)
// ---------------------------------------------------------------------------

export type Light = 'green' | 'yellow' | 'red';
export interface KeyLight { light: Light; label: string; detail?: string }

export const LIGHT_WORDS: Readonly<Record<Light, string>> = {
  green: 'Connected',
  yellow: 'Not checked yet',
  red: 'Rejected / not working',
};

const FRESH_MS = 24 * 60 * 60 * 1000;
/**
 * How far ahead of this browser's clock a server timestamp may be and still count as "just now" (F-67).
 * The server stamps the check with its own clock; a browser a few seconds (or a page whose `now` was
 * captured before the check) behind it must not read a minutes-old check as "from the future".
 */
export const CLOCK_SKEW_MS = 5 * 60 * 1000;

export type CheckAge = 'fresh' | 'stale' | 'unreadable';

/**
 * How old one positive check is. `unreadable` (no timestamp, or one the browser cannot parse) is its
 * own answer so the page never claims "more than a day ago" for a time it could not read (F-67).
 */
export function checkAge(validatedAt: string | null | undefined, now: number = Date.now()): CheckAge {
  const at = Date.parse(validatedAt || '');
  if (!Number.isFinite(at)) return 'unreadable';
  if (at - now > CLOCK_SKEW_MS) return 'unreadable'; // genuinely in the future: not trustworthy either way
  return now - at < FRESH_MS ? 'fresh' : 'stale';
}

interface KeyRecordLike {
  provider?: string | null;
  validation_status?: string | null;
  validation_freshness?: string | null;
  validated_at?: string | null;
}

/**
 * One stored key's light, from the vault record. `checksOff` is true when this deployment
 * has no provider check configured; `cannotVerify` when the provider has no free check.
 */
export function keyLight(
  record: KeyRecordLike,
  { checksOff = false, cannotVerify = false, now = Date.now(), providerId = '' }: { checksOff?: boolean; cannotVerify?: boolean; now?: number; providerId?: string } = {},
): KeyLight {
  const status = record.validation_status || 'unvalidated';
  if (status === 'invalid') {
    const hint = rejectionHint(providerId || (record as { provider?: string }).provider || '');
    return {
      light: 'red',
      label: LIGHT_WORDS.red,
      detail: 'The provider refused this key. Replace it with a new key from your provider account.' + (hint ? ` ${hint}` : ''),
    };
  }
  if (cannotVerify) return { light: 'yellow', label: 'Stored · can’t be checked automatically', detail: 'This provider has no free check. It does not mean the key is wrong.' };
  if (status === 'valid') {
    // The server derives freshness from its own clock; a page whose `now` lags the check must not
    // override a server "recent". Only the server's explicit "not_recently_checked" is an age claim.
    const age = checkAge(record.validated_at, now);
    if (age === 'stale') {
      return { light: 'yellow', label: 'Not recently checked', detail: 'It worked at the last check, more than a day ago.' };
    }
    if (record.validation_freshness === 'not_recently_checked' || (age === 'unreadable' && record.validation_freshness !== 'recent')) {
      // The server also says this for a missing, unreadable or future timestamp: only claim "a day" when the age shows it.
      return { light: 'yellow', label: 'Not recently checked', detail: 'It worked at the last check, but that check is no longer confirmed recent. Test the key again.' };
    }
    return { light: 'green', label: LIGHT_WORDS.green, detail: 'The provider accepted this key at the last check.' };
  }
  if (status === 'unreachable') return { light: 'yellow', label: 'Provider unreachable', detail: 'The provider did not answer the last check. Try again later.' };
  if (checksOff) return { light: 'yellow', label: LIGHT_WORDS.yellow, detail: "Checking isn't available on this deployment yet." };
  return { light: 'yellow', label: LIGHT_WORDS.yellow };
}

/**
 * Provider-specific help shown under a red light (F-68). The provider's own refusal is all the
 * check can see, so this names the likeliest cause without claiming it is the cause.
 */
export const REJECTION_HINTS: Readonly<Record<string, string>> = {
  elevenlabs: 'If this is a restricted ElevenLabs key, it may be missing the "User: Read" permission that Auteur uses to check it. Tick that permission on the key (or make it unrestricted) and test again.',
};

/** The rejection hint for a provider, or '' when there is none. */
export function rejectionHint(providerId: string): string {
  return REJECTION_HINTS[providerId] || '';
}

/** Short names for "One OpenAI key covers writing, images and image analysis" (F-70). */
const COVERAGE_WORDS: Readonly<Record<CapabilityId, string>> = {
  writing: 'writing and chat',
  images: 'images',
  video: 'video',
  voice: 'voice and audio',
  avatars: 'avatars',
  vision: 'image analysis',
  research: 'research',
  other: 'other tools',
};

/**
 * "One OpenAI key covers writing and chat, images and image analysis.", or '' when the provider
 * serves a single kind of work. Planned uses are left out: the line must not promise what is not built.
 */
export function coverageLine(providerId: string, providerLabel: string): string {
  const live = capabilitiesOf(providerId).filter((id) => !isPlannedFor(providerId, id));
  if (live.length < 2) return '';
  const words = live.map((id) => COVERAGE_WORDS[id]);
  const joined = words.length === 2 ? `${words[0]} and ${words[1]}` : `${words.slice(0, -1).join(', ')} and ${words[words.length - 1]}`;
  return `One ${providerLabel} key covers ${joined}. You do not need a separate key for each.`;
}

/** Providers whose name, id or capability titles contain every word of `query` (case-insensitive). */
export function matchesProviderSearch(
  provider: { id: string; label?: string },
  query: string,
): boolean {
  const words = String(query || '').toLowerCase().split(/\s+/).filter(Boolean);
  if (words.length === 0) return true;
  const haystack = [
    provider.label || '', provider.id.replace(/_/g, ' '),
    ...capabilitiesOf(provider.id).map((id) => CAPABILITIES.find((c) => c.id === id)?.title || ''),
  ].join(' ').toLowerCase();
  return words.every((word) => haystack.includes(word));
}

/** A capability group's summary light: the best light among its stored keys, or null when none. */
export function groupLight(lights: readonly KeyLight[]): KeyLight | null {
  if (lights.length === 0) return null;
  if (lights.some((item) => item.light === 'green')) return { light: 'green', label: 'Ready: a key here is connected' };
  if (lights.every((item) => item.light === 'red')) return { light: 'red', label: 'A key here was rejected' };
  return { light: 'yellow', label: 'A key is stored here; not confirmed working right now' };
}

/**
 * The readiness projection's validation state as a light (Settings → Connections rows). The server
 * says `valid` only for a check under 24 hours old when it answered; with `checkedAt` the light also
 * goes yellow once that check ages past 24 hours while the page stays open.
 */
export function readinessLight(state: string | null | undefined, checkedAt: string | null = null, now: number = Date.now()): KeyLight | null {
  switch (state) {
    case 'valid': {
      if (checkedAt !== null && checkAge(checkedAt, now) !== 'fresh') return { light: 'yellow', label: 'Not recently checked' };
      return { light: 'green', label: LIGHT_WORDS.green };
    }
    case 'invalid': return { light: 'red', label: LIGHT_WORDS.red };
    case null: case undefined: case '': return null;
    case 'cannot_verify': return { light: 'yellow', label: 'Can’t be checked automatically' };
    case 'unreachable': return { light: 'yellow', label: 'Provider unreachable' };
    case 'not_recently_checked': return { light: 'yellow', label: 'Not recently checked' };
    default: return { light: 'yellow', label: LIGHT_WORDS.yellow };
  }
}

// ---------------------------------------------------------------------------
// "Used for" in plain words (F-22)
// ---------------------------------------------------------------------------

const OPERATION_WORDS: Readonly<Record<string, string>> = {
  'chat/completions': 'writing and chat',
  'images/generations': 'image generation',
  'image/generate': 'image generation',
  'vision/describe': 'image analysis',
  'video/generate': 'video generation',
  'audio/speech': 'narration and voices',
  'audio/dialogue': 'multi-voice dialogue',
  'audio/sound_effect': 'sound effects',
  'audio/music': 'music',
};

/** Plain words for one server operation id; `null` for plumbing (fetching a finished result). */
export function operationWords(operation: string, providerId = ''): string | null {
  if (operation === 'video/result') return null;
  if (operation === 'video/generate' && providerId === 'heygen') return 'avatar video';
  return OPERATION_WORDS[operation] || operation;
}

/** "writing and chat, image generation" (deduplicated, in order). */
export function usedForWords(operations: readonly string[], providerId = ''): string {
  const words: string[] = [];
  for (const operation of operations) {
    const word = operationWords(operation, providerId);
    if (word && !words.includes(word)) words.push(word);
  }
  return words.join(', ');
}
